import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminSuratPeringatanPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeringatan, setSelectedPeringatan] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tipeFilter, setTipeFilter] = useState('');

  // Real data for surat peringatan
  const [suratPeringatanRequests, setSuratPeringatanRequests] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  // Fetch surat peringatan from API
  const fetchSuratPeringatan = async (page = 1, search = '', date = '', status = '', tipe = '') => {
    try {
      setLoading(true);
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      let url = `${apiBase}/api/admin/surat-peringatan?page=${page}&per_page=${itemsPerPage}`;
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (date.trim()) {
        url += `&date_filter=${encodeURIComponent(date.trim())}`;
      }
      if (status.trim()) {
        url += `&status_filter=${encodeURIComponent(status.trim())}`;
      }
      if (tipe.trim()) {
        url += `&type_filter=${encodeURIComponent(tipe.trim())}`;
      }
      
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch surat peringatan: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      
      // Laravel pagination response structure
      const sourceData = data.data || [];
      
      setSuratPeringatanRequests(sourceData);
      
      // Set pagination info from Laravel pagination
      setTotalPages(data.last_page || 1);
      setTotalItems(data.total || sourceData.length);
    } catch (e) {
      console.error('Error fetching surat peringatan:', e);
      setNotification({
        show: true,
        message: `Error fetching data: ${e.message}`,
        type: 'error'
      });
      setSuratPeringatanRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin users
  const fetchAdminUsers = async () => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const res = await authFetch(`${apiBase}/api/admin_users`);
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.data || data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  useEffect(() => {
    fetchSuratPeringatan(currentPage, searchQuery, dateFilter, statusFilter, tipeFilter);
    fetchAdminUsers();
  }, [currentPage, searchQuery, dateFilter, statusFilter, tipeFilter]);

  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student } });
    }
  };

  // Server-side pagination - display data directly from API
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentPageItems = suratPeringatanRequests;

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, tipeFilter, dateFilter]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get admin name by decision_by_id
  const getDecisionMakerName = (decisionById) => {
    if (!decisionById || !adminUsers.length) return null;
    const admin = adminUsers.find(admin => admin.id === decisionById);
    return admin ? admin.name : null;
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilter) params.append('date_filter', dateFilter);
      if (statusFilter) params.append('status_filter', statusFilter);
      if (tipeFilter) params.append('type_filter', tipeFilter);

      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      console.log('Making Excel export request to:', `${apiBase}/api/admin/surat-peringatan/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const response = await authFetch(`${apiBase}/api/admin/surat-peringatan/export-excel?${params.toString()}`, {
        method: 'PUT',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Surat Peringatan');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Surat_Peringatan_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename} (${data.data.length} records)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Gagal mengekspor data ke Excel';
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Sesi telah berakhir. Silakan login kembali.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Tidak memiliki akses. Silakan login kembali.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Tidak memiliki izin untuk mengekspor data.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Endpoint ekspor tidak ditemukan. Hubungi administrator.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Terjadi kesalahan server. Coba lagi nanti.';
      } else if (error.message.includes('timeout') || error.message.includes('network')) {
        errorMessage = 'Koneksi timeout. Periksa koneksi internet Anda.';
      }
      
      setNotification({
        show: true,
        message: `${errorMessage} (Detail: ${error.message})`,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Surat Peringatan</h1>
            <Subtitle>Kelola dan tinjau surat peringatan siswa berdasarkan poin apresiasi</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <SearchContainer>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Cari berdasarkan nama siswa, kelas, atau tipe..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchContainer>
              
              <FiltersContainer>
                <FilterGroup>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    placeholder="Filter tanggal"
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="menunggu">Menunggu</option>
                    <option value="terima">Diterima</option>
                    <option value="tolak">Ditolak</option>
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterSelect
                    value={tipeFilter}
                    onChange={(e) => setTipeFilter(e.target.value)}
                  >
                    <option value="">Semua Tipe</option>
                    <option value="SP1">SP1</option>
                    <option value="SP2">SP2</option>
                    <option value="SP3">SP3</option>
                  </FilterSelect>
                </FilterGroup>
                <ExportButton onClick={handleExcelExport}>
                  📊 Export Excel
                </ExportButton>
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal</th>
                    <th>Nama Murid</th>
                    <th>Kelas</th>
                    <th>Tipe</th>
                    <th>Total Poin</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPeringatan(request)}
                        selected={selectedPeringatan?.id === request.id}
                      >
                        <td>{request.tanggal}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.namaMurid}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>
                        <td>
                          <TypeBadge type={request.tipe}>
                            {request.tipe}
                          </TypeBadge>
                        </td>
                        <td>{request.poin}</td>
                        <td>
                          <StatusBadge status={request.status}>
                            {request.status === 'menunggu' ? 'Menunggu' :
                              request.status === 'terima' ? 'Diterima' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPeringatan(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada surat peringatan
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalItems > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} surat peringatan
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          active={currentPage === pageNum}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPeringatan && (
        <DetailModal onClick={() => setSelectedPeringatan(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <SuratPeringatanEditModal
              selectedPeringatan={selectedPeringatan}
              setSelectedPeringatan={setSelectedPeringatan}
              getDecisionMakerName={getDecisionMakerName}
            />
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Styled components
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 150px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui':
      case 'terima': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui':
      case 'terima': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  
  ${props => {
    switch (props.type) {
      case 'SP1':
        return `
          background-color: #fef3c7;
          color: #92400e;
        `;
      case 'SP2':
        return `
          background-color: #fed7aa;
          color: #9a3412;
        `;
      case 'SP3':
        return `
          background-color: #fee2e2;
          color: #991b1b;
        `;
      default:
        return `
          background-color: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 1rem;
  color: var(--text-dark);
  font-weight: 500;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.938rem;
  color: var(--text-dark);
  background-color: var(--background);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

// Editable modal for Surat Peringatan
function SuratPeringatanEditModal({ selectedPeringatan, setSelectedPeringatan, getDecisionMakerName }) {
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminNote, setAdminNote] = useState(selectedPeringatan?.admin_note || '');

  // Function to update surat peringatan status
  const updatePeringatanStatus = async (status) => {
    if (!selectedPeringatan || !selectedPeringatan.id) return;
    
    setIsSubmitting(true);
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      const requestData = { status, admin_note: adminNote };
      
      console.log('Sending request data:', requestData);
      
      const res = await authFetch(`${apiBase}/api/admin/surat-peringatan/${selectedPeringatan.id}/status`, { 
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
      }
      
      const updatedPeringatan = await res.json();
      console.log('Status updated:', updatedPeringatan);
      
      setNotification({
        show: true,
        message: status === 'terima' ? 'Surat peringatan berhasil diterima!' : 'Surat peringatan ditolak!',
        type: status === 'terima' ? 'success' : 'info'
      });
      
      // Close notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
        // Refresh the data
        window.location.reload();
      }, 3000);
      
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        show: true,
        message: `Error: ${error.message}`,
        type: 'error'
      });
      
      // Close notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
      
      <DetailHeader>
        <DetailHeaderContent>
          <h2>Detail Surat Peringatan</h2>
          <StatusContainer>
            <StatusBadge status={selectedPeringatan.status}>
              {selectedPeringatan.status === 'menunggu' ? 'Menunggu' :
                selectedPeringatan.status === 'terima' ? 'Diterima' : 'Ditolak'}
            </StatusBadge>
            {selectedPeringatan.status !== 'menunggu' && (
              <DecisionMaker>
                Diputuskan oleh: {getDecisionMakerName(selectedPeringatan.decision_by_id) || 'Herman Yosef'}
              </DecisionMaker>
            )}
          </StatusContainer>
        </DetailHeaderContent>
        <CloseButton onClick={() => setSelectedPeringatan(null)}>×</CloseButton>
      </DetailHeader>

      <DetailContent>
        <DetailSection>
          <DetailGrid>
            <DetailGroup>
              <DetailLabel>Tanggal</DetailLabel>
              <DetailValue>{selectedPeringatan.tanggal}</DetailValue>
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Nama Murid</DetailLabel>
              <DetailValue>{selectedPeringatan.namaMurid}</DetailValue>
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>Kelas</DetailLabel>
              <DetailValue>{selectedPeringatan.kelas}</DetailValue>
            </DetailGroup>
            <DetailGroup>
              <DetailLabel>NIS</DetailLabel>
              <DetailValue>{selectedPeringatan.nis}</DetailValue>
            </DetailGroup>
          </DetailGrid>
        </DetailSection>

      <DetailSection>
        <DetailGrid>
          <DetailGroup>
            <DetailLabel>Tipe Peringatan</DetailLabel>
            <DetailValue>
              <TypeBadge type={selectedPeringatan.tipe}>
                {selectedPeringatan.tipe}
              </TypeBadge>
            </DetailValue>
          </DetailGroup>
          <DetailGroup>
            <DetailLabel>Total Poin</DetailLabel>
            <DetailValue>{selectedPeringatan.poin}</DetailValue>
          </DetailGroup>
        </DetailGrid>
      </DetailSection>

      <DetailSection>
        <DetailGroup>
          <DetailLabel>Catatan Admin</DetailLabel>
          <NoteTextarea
            placeholder="Tambahkan catatan untuk keputusan ini (opsional)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
          />
        </DetailGroup>
      </DetailSection>

        <ActionButtons>
          <RejectButton 
            type="button" 
            onClick={() => updatePeringatanStatus('tolak')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Tolak'}
          </RejectButton>
          <ApproveButton 
            type="button" 
            onClick={() => updatePeringatanStatus('terima')} 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Terima'}
          </ApproveButton>
        </ActionButtons>
      </DetailContent>
    </>
  );
}

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 
    props.type === 'error' ? 'var(--danger, #c01c28)' : 'var(--info, #3b82f6)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

// Pagination styled components
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminSuratPeringatanPage;